import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, buildApiUrl } from '../config/api';

export default function ReportsServicesPerServiser() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [allTime, setAllTime] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.SERVICES_PER_SERVISER, {
          page: String(page),
          pageSize: String(pageSize),
          month: String(month),
          year: String(year),
          allTime: allTime ? '1' : '',
          ...(q.trim() && { q: q.trim() })
        });
        const resp = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) throw new Error('Greška pri učitavanju izvještaja.');
        const data = await resp.json();
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(Number(data.total || 0));
        setTotalPages(Number(data.totalPages || 1));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page, pageSize, month, year, allTime, q]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(r => (`${r.first_name || ''} ${r.last_name || ''}`.trim()).toLowerCase().includes(term));
  }, [items, q]);

  const handleDownload = async (format) => {
    const token = localStorage.getItem('token');
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.SERVICES_PER_SERVISER, {
      format,
      month: String(month),
      year: String(year),
      allTime: allTime ? '1' : '',
      ...(q.trim() && { q: q.trim() })
    });
    try {
      const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Neuspješno preuzimanje.');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = allTime 
        ? (format === 'pdf' ? 'servisi-po-serviserima-all-time.pdf' : 'servisi-po-serviserima-all-time.xlsx')
        : (format === 'pdf' ? `servisi-po-serviserima-${year}-${String(month).padStart(2,'0')}.pdf` : `servisi-po-serviserima-${year}-${String(month).padStart(2,'0')}.xlsx`);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  // Ne prekidaj prikaz tokom učitavanja da se ne gubi fokus iz inputa
  // Grešku prikaži u okviru stranice
  const errorBanner = error ? (
    <div className="mb-3 px-3 py-2 rounded-md bg-red-50 text-red-700 border border-red-200">{error}</div>
  ) : null;

  const years = Array.from({ length: 15 }, (_, i) => now.getFullYear() - 10 + i);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          Broj servisa po serviserima
          {loading && (
            <span className="text-xs font-normal text-gray-500">Učitavanje...</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => handleDownload('pdf')} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700" title="Preuzmi PDF">
            <FontAwesomeIcon icon={faFilePdf} /> PDF
          </button>
          <button onClick={() => handleDownload('xlsx')} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700" title="Preuzmi Excel">
            <FontAwesomeIcon icon={faFileExcel} /> Excel
          </button>
        </div>
      </div>
      {errorBanner}

      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-xs text-gray-500">Filter za prilagođene izvještaje</span>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Mjesec:</label>
            <select value={month} onChange={(e) => { setMonth(Number(e.target.value)); setPage(1); }} className="px-2 py-2 border border-gray-300 rounded-md">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Godina:</label>
            <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setPage(1); }} className="px-2 py-2 border border-gray-300 rounded-md">
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={allTime} onChange={(e) => { setAllTime(e.target.checked); setPage(1); }} />
            Svi periodi
          </label>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Pretraga po imenu ili prezimenu..."
            className="w-full sm:w-96 px-3 py-2 border border-gray-300 rounded-md"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Redova po stranici:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="px-2 py-2 border border-gray-300 rounded-md"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600">Ukupno: {total}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviser</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Broj servisa</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.map((row) => (
              <tr key={row.id_user} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-900">{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.services_count}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">Nema podataka.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">Stranica {page} od {totalPages}</div>
        <div className="space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className={`px-3 py-2 rounded-md border ${page <= 1 ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            Prethodna
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className={`px-3 py-2 rounded-md border ${page >= totalPages ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            Sljedeća
          </button>
        </div>
      </div>
    </div>
  );
}